import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney } from "@/api";
import { toast } from "sonner";
import {
  PhoneCall, FileText, UserCheck, DollarSign, ArrowRight,
  StickyNote, History, XCircle, AlertTriangle,
} from "lucide-react";

const STAGE_META = {
  none:      { label: "Unstarted",      color: "text-slate-400",   bg: "border-slate-800",         accent: "bg-slate-700"    },
  discovery: { label: "Discovery",      color: "text-amber-300",   bg: "border-amber-800/60",      accent: "bg-amber-500/80" },
  proposal:  { label: "Proposal",       color: "text-orange-300",  bg: "border-orange-800/60",     accent: "bg-orange-500/80"},
  won:       { label: "Won · Client",   color: "text-emerald-300", bg: "border-emerald-800/60",    accent: "bg-emerald-500/80"},
  lost:      { label: "Lost · Closed",  color: "text-rose-300",    bg: "border-rose-800/60",       accent: "bg-rose-500/80"  },
};

const NEXT_STAGES = {
  none:      [{ to: "discovery", label: "Start Discovery" }],
  discovery: [{ to: "proposal",  label: "Advance to Proposal" }, { to: "lost", label: "Mark Lost" }],
  proposal:  [{ to: "won",       label: "Mark Won · Client" }, { to: "lost", label: "Mark Lost" }],
  won:       [], // duplicate Won requires override reason via re-affirm
  lost:      [],
};

function StageBadge({ stage }) {
  const m = STAGE_META[stage] || STAGE_META.none;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono border bg-slate-950/40 ${m.bg} ${m.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${m.accent}`} />
      {m.label}
    </span>
  );
}

function AdvanceDialog({ opp, toStage, onCancel, onConfirm, pending, requireOverride }) {
  const [proposalValue, setProposalValue] = useState(opp?.proposal_value || "");
  const [bookedRevenue, setBookedRevenue] = useState(opp?.booked_revenue || "");
  const [note, setNote] = useState("");
  const [override, setOverride] = useState("");

  const need = requireOverride || toStage === "won" || (toStage === "proposal");
  const submit = () => {
    const payload = { reason: note || undefined };
    if (toStage === "proposal") payload.proposal_value = parseFloat(proposalValue) || 0;
    if (toStage === "won" && !requireOverride) payload.booked_revenue = parseFloat(bookedRevenue) || 0;
    if (requireOverride) payload.override_reason = override;
    onConfirm(payload);
  };
  const disabled =
    pending ||
    (requireOverride && !override.trim()) ||
    (toStage === "won" && !requireOverride && !bookedRevenue);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="advance-dialog">
      <div className="bg-slate-950 border border-slate-800 rounded-lg max-w-md w-full p-5 space-y-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">
            {requireOverride ? "Re-affirm Won" : "Advance lifecycle"}
          </div>
          <h3 className="text-lg font-medium text-slate-100">{opp?.prospect_name}</h3>
          <div className="flex items-center gap-2 mt-2 text-sm">
            <StageBadge stage={opp?.lifecycle_stage || "none"} />
            <ArrowRight size={14} className="text-slate-500" />
            <StageBadge stage={toStage} />
          </div>
        </div>

        {requireOverride && (
          <div className="border border-amber-900/60 bg-amber-950/30 rounded-md p-3 text-xs text-amber-200 flex gap-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>This opportunity is already Won. Client count will not increase. Provide an override reason for the audit log.</span>
          </div>
        )}

        {toStage === "proposal" && !requireOverride && (
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Proposal value (USD)</label>
            <input
              type="number"
              value={proposalValue}
              onChange={(e) => setProposalValue(e.target.value)}
              placeholder="e.g. 18000"
              data-testid="advance-proposal-value-input"
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 font-mono focus:outline-none focus:ring-1 focus:ring-orange-500"
            />
            <div className="text-[11px] text-slate-500 mt-1">Proposed deal value. Does not count as booked revenue.</div>
          </div>
        )}

        {toStage === "won" && !requireOverride && (
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Booked revenue (USD)</label>
            <input
              type="number"
              value={bookedRevenue}
              onChange={(e) => setBookedRevenue(e.target.value)}
              placeholder="e.g. 12000"
              data-testid="advance-booked-revenue-input"
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
            <div className="text-[11px] text-slate-500 mt-1">Contracted/booked revenue for this win. Powers Dashboard Revenue KPI.</div>
          </div>
        )}

        {requireOverride && (
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Override reason (required)</label>
            <textarea
              rows={2}
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              placeholder="e.g. Expansion booking on same client"
              data-testid="advance-override-input"
              className="w-full bg-slate-900 border border-amber-800/60 rounded-md px-3 py-2 text-sm text-amber-100 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
          </div>
        )}

        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Note (optional)</label>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Context for the audit history"
            data-testid="advance-note-input"
            className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onCancel}
            data-testid="advance-cancel-btn"
            className="px-3 py-1.5 rounded-md text-xs font-medium border bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700"
          >Cancel</button>
          <button
            onClick={submit}
            disabled={disabled}
            data-testid="advance-confirm-btn"
            className="px-3 py-1.5 rounded-md text-xs font-medium border bg-emerald-800 text-emerald-100 border-emerald-600 hover:brightness-125 disabled:opacity-40"
          >Confirm</button>
        </div>
      </div>
    </div>
  );
}

function ManualEventPanel({ opportunities }) {
  const [oppId, setOppId] = useState("");
  const [eventLabel, setEventLabel] = useState("note");
  const [note, setNote] = useState("");
  const qc = useQueryClient();
  const log = useMutation({
    mutationFn: async () => (await api.post("/decisions", {
      action: "log_manual_event", target_type: "opportunity", target_id: oppId,
      event_label: eventLabel, reason: note,
    })).data,
    onSuccess: () => {
      toast.success("Manual event logged (KPIs unaffected)");
      setOppId(""); setNote("");
      qc.invalidateQueries({ queryKey: ["outcomes"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to log event"),
  });
  return (
    <div className="border border-slate-800 bg-slate-900/60 rounded-lg p-4 space-y-3" data-testid="manual-event-panel">
      <div className="flex items-center gap-2">
        <StickyNote size={14} className="text-slate-400" />
        <h2 className="text-base font-medium text-slate-200 tracking-tight">Log manual event</h2>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-slate-500">does not affect KPIs</span>
      </div>
      <select
        value={oppId}
        onChange={(e) => setOppId(e.target.value)}
        data-testid="manual-event-opp-select"
        className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-500"
      >
        <option value="">Select opportunity…</option>
        {opportunities.map((o) => (
          <option key={o.id} value={o.id}>{o.prospect?.name} · {o.status.replace("_", " ")}</option>
        ))}
      </select>
      <select
        value={eventLabel}
        onChange={(e) => setEventLabel(e.target.value)}
        data-testid="manual-event-label-select"
        className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-500"
      >
        <option value="note">Note</option>
        <option value="offline_touchpoint">Offline touchpoint</option>
        <option value="referral">Referral</option>
        <option value="event_signal">Event / signal</option>
      </select>
      <textarea
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Describe the exceptional/offline event"
        data-testid="manual-event-note-input"
        className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-500"
      />
      <button
        onClick={() => log.mutate()}
        disabled={log.isPending || !oppId}
        data-testid="log-manual-event-btn"
        className="w-full bg-slate-800 hover:bg-slate-700 border border-slate-600 disabled:opacity-40 text-slate-200 text-sm font-medium rounded-md px-3 py-2 transition-colors"
      >Log event</button>
    </div>
  );
}

export default function Outcomes() {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null); // { opp, toStage, requireOverride }

  const { data, isLoading } = useQuery({
    queryKey: ["outcomes"],
    queryFn: async () => (await api.get("/outcomes")).data,
  });
  const { data: opportunities = [] } = useQuery({
    queryKey: ["opportunities"],
    queryFn: async () => (await api.get("/opportunities")).data,
  });

  const advance = useMutation({
    mutationFn: async ({ oppId, toStage, extra }) => (await api.post("/decisions", {
      action: "advance_stage", target_type: "opportunity", target_id: oppId,
      to_stage: toStage, ...extra,
    })).data,
    onSuccess: (d) => {
      toast.success(`Advanced to ${d.recorded_outcome?.to_stage}`);
      setDialog(null);
      qc.invalidateQueries({ queryKey: ["outcomes"] });
      qc.invalidateQueries({ queryKey: ["opportunities"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) => {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : detail;
      toast.error(msg || "Advance failed");
      if (detail?.requires === "override_reason") {
        // switch dialog to override mode
        setDialog((d) => d ? { ...d, requireOverride: true } : d);
      }
    },
  });

  const totals = data?.totals || { discovery: 0, proposals: 0, clients: 0, revenue: 0, proposal_value_open: 0 };
  const pipeline = data?.pipeline || { discovery: [], proposal: [], won: [], lost: [], none: [] };
  const history = data?.history || [];

  const kpis = [
    { label: "Discovery", value: totals.discovery, icon: PhoneCall,  color: "text-amber-300",   testid: "metric-discovery",
      sub: "unique opportunities" },
    { label: "Proposals", value: totals.proposals, icon: FileText,   color: "text-orange-300",  testid: "metric-proposals",
      sub: `open value ${fmtMoney(totals.proposal_value_open)}` },
    { label: "Clients",   value: totals.clients,   icon: UserCheck,  color: "text-emerald-300", testid: "metric-clients",
      sub: "opportunities won" },
    { label: "Booked Revenue", value: fmtMoney(totals.revenue), icon: DollarSign, color: "text-emerald-400", testid: "metric-revenue",
      sub: "sum of won bookings" },
  ];

  const columns = [
    { key: "discovery", opps: pipeline.discovery || [] },
    { key: "proposal",  opps: pipeline.proposal  || [] },
    { key: "won",       opps: pipeline.won       || [] },
    { key: "lost",      opps: pipeline.lost      || [] },
  ];

  const unstarted = pipeline.none || [];

  const openDialog = (opp, toStage, requireOverride = false) =>
    setDialog({ opp, toStage, requireOverride });

  return (
    <div className="space-y-6" data-testid="outcomes-view">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-100">Outcomes</h1>
        <p className="text-sm text-slate-400 mt-1">Advance opportunities through the commercial lifecycle. KPIs reflect unique opportunity state, not raw events.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((c) => (
          <div key={c.label} className="border border-slate-800 bg-slate-900/60 rounded-lg p-4 space-y-1" data-testid={c.testid}>
            <div className="flex items-center gap-2">
              <c.icon size={14} className={c.color} />
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{c.label}</span>
            </div>
            <div className={`font-mono text-2xl font-semibold ${c.color}`}>{c.value}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-600">{c.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-3" data-testid="lifecycle-board">
        {columns.map(({ key, opps }) => {
          const meta = STAGE_META[key];
          return (
            <div key={key} className={`border ${meta.bg} bg-slate-900/40 rounded-lg`} data-testid={`lifecycle-col-${key}`}>
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800/60">
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${meta.accent}`} />
                  <span className={`font-mono text-[11px] uppercase tracking-widest ${meta.color}`}>{meta.label}</span>
                </div>
                <span className="font-mono text-[10px] text-slate-500">{opps.length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[80px]">
                {opps.length === 0 && <div className="text-[11px] text-slate-600 font-mono px-1 py-2">— empty —</div>}
                {opps.map((o) => (
                  <div
                    key={o.opportunity_id}
                    data-testid={`lifecycle-card-${o.opportunity_id}`}
                    className="border border-slate-800 bg-slate-950/60 rounded-md p-2.5 space-y-1.5"
                  >
                    <div className="text-sm text-slate-100 font-medium">{o.prospect_name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      {key === "proposal" && `proposed ${fmtMoney(o.proposal_value)}`}
                      {key === "won" && `booked ${fmtMoney(o.booked_revenue)}`}
                      {key === "discovery" && (o.outreach_status || "in flight")}
                      {key === "lost" && "closed"}
                    </div>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {(NEXT_STAGES[key] || []).map((n) => (
                        <button
                          key={n.to}
                          onClick={() => openDialog(o, n.to)}
                          data-testid={`advance-${o.opportunity_id}-${n.to}-btn`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border bg-emerald-950/40 text-emerald-300 border-emerald-800/60 hover:brightness-125"
                        >
                          <ArrowRight size={10} /> {n.label}
                        </button>
                      ))}
                      {key === "won" && (
                        <button
                          onClick={() => openDialog(o, "won", true)}
                          data-testid={`reaffirm-won-${o.opportunity_id}-btn`}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border bg-amber-950/40 text-amber-300 border-amber-800/60 hover:brightness-125"
                        >
                          Re-affirm Won (override)
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {unstarted.length > 0 && (
        <div className="border border-slate-800 bg-slate-900/60 rounded-lg p-3" data-testid="unstarted-panel">
          <div className="flex items-center justify-between px-1 pb-2">
            <span className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Unstarted lifecycle — start Discovery when the conversation warrants it</span>
            <span className="font-mono text-[10px] text-slate-500">{unstarted.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
            {unstarted.map((o) => (
              <div key={o.opportunity_id} className="border border-slate-800 bg-slate-950/60 rounded-md p-2.5 flex items-center justify-between gap-2"
                   data-testid={`unstarted-card-${o.opportunity_id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-slate-100 font-medium truncate">{o.prospect_name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{o.outreach_status}</div>
                </div>
                <button
                  onClick={() => openDialog(o, "discovery")}
                  data-testid={`start-discovery-${o.opportunity_id}-btn`}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium border bg-amber-950/40 text-amber-300 border-amber-800/60 hover:brightness-125 shrink-0"
                >
                  <ArrowRight size={10} /> Start Discovery
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 border border-slate-800 bg-slate-900/60 rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-800/60">
            <History size={14} className="text-slate-400" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Immutable transition history</span>
          </div>
          <table className="w-full text-left" data-testid="outcomes-history-table">
            <thead>
              <tr className="border-b border-slate-800 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2">Prospect</th>
                <th className="px-3 py-2">Transition</th>
                <th className="px-3 py-2 text-right">Value</th>
                <th className="px-3 py-2 text-right">Recorded</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={5} className="px-3 py-4 text-sm text-slate-500">Loading…</td></tr>}
              {history.map((o) => (
                <tr key={o.id} className="py-2 px-3 text-xs border-b border-slate-800/50 hover:bg-slate-800/40 font-mono text-slate-300"
                    data-testid={`history-row-${o.id}`}>
                  <td className="px-3 py-2">
                    {o.event === "manual_event" ? (
                      <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-mono border bg-slate-800/60 text-slate-400 border-slate-700">
                        manual · {o.event_label}
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded text-[10px] font-mono border bg-sky-950/40 text-sky-300 border-sky-800/60">
                        advance
                      </span>
                    )}
                    {o.override_reason && (
                      <span className="ml-1 inline-flex px-2 py-0.5 rounded text-[10px] font-mono border bg-amber-950/40 text-amber-300 border-amber-800/60"
                            title={o.override_reason}>override</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-sans text-slate-200">{o.prospect_name}</td>
                  <td className="px-3 py-2">
                    {o.event === "advance_stage" ? (
                      <span className="text-slate-400">{(o.from_stage || "—")} → <span className="text-slate-100">{o.to_stage}</span></span>
                    ) : (
                      <span className="text-slate-500 font-sans truncate">{o.note}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-emerald-300">
                    {o.booked_revenue ? fmtMoney(o.booked_revenue) : o.proposal_value ? `${fmtMoney(o.proposal_value)} (proposed)` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-500">{new Date(o.recorded_at).toLocaleString()}</td>
                </tr>
              ))}
              {history.length === 0 && !isLoading && (
                <tr><td colSpan={5} className="px-3 py-4 text-sm text-slate-500">No lifecycle events recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <ManualEventPanel opportunities={opportunities} />
      </div>

      {dialog && (
        <AdvanceDialog
          opp={dialog.opp}
          toStage={dialog.toStage}
          requireOverride={dialog.requireOverride}
          pending={advance.isPending}
          onCancel={() => setDialog(null)}
          onConfirm={(extra) => advance.mutate({ oppId: dialog.opp.opportunity_id, toStage: dialog.toStage, extra })}
        />
      )}
    </div>
  );
}
